<template>
  <b-modal
    :id="id"
    hide-footer
    hide-header
    centered
    scrollable
    no-close-on-backdrop
    size="xl"
    body-class="position-relative"
  >
    <template #default="{ close }">
      <!-- アップロード中のローディング表示 -->
      <loading-upload v-if="isUploading" :msg="loadingMsg" :fullscreen="false" />

      <b-row class="d-flex my-1 ml-0 mr-1 justify-content-between align-items-center">
        <div>{{ navTitle }}</div>
        <div @click="showEditDiscardAlert(close)"><b-icon-x class="x-btn"></b-icon-x></div>
      </b-row>
      <message-edit
        ref="messageEdit"
        :type="type"
        :domainId="domainId"
        :topicId="topicId"
        :messageId="messageId"
        :title="title"
        :message="message"
        :photos="photos"
        :allow_attachment_type="allow_attachment_type"
        :acl="acl"
        :topicAcl="topicAcl"
        :talkId="talkId"
        :sendTalkOnCreate="sendTalkOnCreate"
        :sendTalkOnUpdate="sendTalkOnUpdate"
        :isModal="true"
        v-on:cancelToModal="close"
        v-on:confirmToModal="onEdit(close)"
        v-on:interruption="onInterruption"
        v-on:uploading="onUploading"
      />
    </template>
  </b-modal>
</template>

<style lang="scss">
#modal-message {
  .modal-body {
    display: flex;
    flex-direction: column;
    max-height: 580px;
    overflow-y: hidden;
  }
}

.x-btn {
  display: flex;
  width: 35px;
  height: 35px;
  color: #666666;
  cursor: pointer;
}

.post-btn {
  display: flex;
  margin-left: auto;
  background-color: #417cff;
  font-size: 13px;
  align-items: center;
  justify-content: center;
}

.modal-fullscreen .modal-dialog {
  border: 0;
  width: 100%;
  margin: 0;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100vh;
  position: fixed;
  display: flex;
}
</style>

<script lang="ts">
import { computed, defineComponent, ref, type PropType } from "vue";
import { Acl } from "../model";
import type { Attachment } from "../model";
import MessageEdit from "./MessageEdit.vue";
import LoadingUpload from "./loading/LoadingUpload.vue";
import type { AttachmentFileTypes } from "@/suppport-attachment-types";
import { AttachmentFileTypesDefault, AttachmentFileTypesNone } from "@/suppport-attachment-types";
import { useEditDiscard } from "@/composables/useEditDiscard";

export default defineComponent({
  name: "message-edit-modal",
  components: {
    MessageEdit,
    LoadingUpload,
  },
  props: {
    id: { type: String, default: () => "", required: true },
    type: { type: String, default: () => "", required: true },
    domainId: { type: String, default: () => "" },
    topicId: { type: String, default: () => "" },
    messageId: { type: String, default: () => "" },
    title: { type: String, default: () => "" },
    message: { type: String, default: () => "" },
    photos: { type: Array as PropType<Attachment[]>, default: () => [] },
    acl: { type: Object as PropType<Acl>, default: () => Acl.createDummy() },
    topicAcl: { type: Object as PropType<Acl>, default: () => Acl.createDummy() },
    talkId: { type: String, default: () => "" },
    sendTalkOnCreate: { type: Boolean, default: () => false },
    sendTalkOnUpdate: { type: Boolean, default: () => false },
    // Direct組織設定
    allow_attachment_type: {
      // 元 @Prop は runtime type 引数なし（型注釈のみ）。検証を増やさないため type は付けない。
      default: () => AttachmentFileTypesDefault as AttachmentFileTypes,
    },
  },
  setup(props, { emit }) {
    // 旧 EditDiscardMixin → composable（1-D 利用側書き換え）
    const { showEditDiscardAlert } = useEditDiscard();

    /* アップローディング関係 */
    const isUploading = ref<boolean>(false);
    const loadingMsg = ref<string>("");

    // 添付可能か
    const allow_attachment = computed((): boolean => {
      return props.allow_attachment_type != AttachmentFileTypesNone;
    });

    // 画面タイトル
    const navTitle = computed((): string => {
      return props.type == "create" ? "投稿の作成" : "投稿の編集";
    });

    /* 投稿処理 */
    const onEdit = (closeEvent: () => void): void => {
      closeEvent();
    };

    /* 中断処理(モーダルが消えた際に) */
    const onInterruption = (param: { id: string; path: string }): void => {
      console.log("interruption process");
      emit("interruptionViaModal", param);
    };

    const onUploading = (param: { isUploading: boolean; loadingMsg: string }): void => {
      isUploading.value = param.isUploading;
      loadingMsg.value = param.loadingMsg;
    };

    return {
      showEditDiscardAlert,
      isUploading,
      loadingMsg,
      allow_attachment,
      navTitle,
      onEdit,
      onInterruption,
      onUploading,
    };
  },
});
</script>
